import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Calendar,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Eye,
  Globe,
  ListChecks,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  Send,
  Sparkles,
  Tag,
  ThumbsDown,
  ThumbsUp,
  Upload,
  Users,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { EmptyPanel, PagePanel, PageShell } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { cn, formatPhone } from "@/lib/utils";

interface BuyerInfo { id: string; first_name: string; last_name: string; email: string; phone: string | null; status?: string | null; }

// Account statuses where the buyer is locked out of the platform, so the advisor
// can no longer take action (message) on them. Mirrors the backend login guard.
const DEACTIVATED_STATUSES = ["inactive"];
const isDeactivated = (status?: string | null) => !!status && DEACTIVATED_STATUSES.includes(status);
const statusLabel = (status?: string | null) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
interface AssignmentHistoryEntry { id: number; advisor_name: string; action: string; note: string | null; created_at: string; }

interface LeadDetail {
  id: string;
  reference_code: string | null;
  image_url: string | null;
  title: string | null;
  company_name: string | null;
  owner_email: string | null;
  website_url: string | null;
  business_description: string | null;
  industry_category: string | null;
  potential_category: string | null;
  location: string | null;
  interest_count?: number;
  created_at?: string;
}

interface Assignment {
  id: number;
  pipeline_id: string;
  pipeline_reference_code: string | null;
  pipeline_title: string;
  buyer: BuyerInfo;
  assignment_status: string;
  buyer_message: string | null;
  assigned_at: string | null;
  responded_at: string | null;
  created_at: string;
  assignment_history: AssignmentHistoryEntry[];
}

interface PipelineMessage {
  id: number;
  interest_id: number;
  sender_id: string;
  sender_role: string;
  sender_name: string;
  body: string;
  is_read_by_buyer: boolean;
  is_read_by_advisor: boolean;
  created_at: string;
  edited_at: string | null;
}

const STATUS_STYLE: Record<string, { pill: string; dot: string }> = {
  pending:  { pill: "bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/30",  dot: "bg-amber-400" },
  accepted: { pill: "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/30", dot: "bg-emerald-500" },
  rejected: { pill: "bg-red-500/10 text-red-600 ring-1 ring-red-500/30",        dot: "bg-red-400" },
};

function initials(b: BuyerInfo) {
  return `${b.first_name[0] ?? ""}${b.last_name[0] ?? ""}`.toUpperCase();
}

function statusStyle(status: string) {
  return STATUS_STYLE[status] ?? { pill: "bg-slate-100 text-slate-600 ring-1 ring-slate-200", dot: "bg-slate-400" };
}

function StatusPill({ status }: { status: string }) {
  const s = statusStyle(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status}
    </span>
  );
}

// ── Message thread (buyer ↔ advisor) ──────────────────────────────────────
function MessageThread({ interestId, canMessage, buyerSuspended, buyerStatus }: { interestId: number; canMessage: boolean; buyerSuspended?: boolean; buyerStatus?: string | null }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<PipelineMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const load = async () => {
    try {
      const data = await api.get<PipelineMessage[]>(`/lead-pipeline/interests/${interestId}/messages`);
      setMessages(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [interestId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const msg = await api.post<PipelineMessage>(`/lead-pipeline/interests/${interestId}/messages`, { body: trimmed });
      setMessages((prev) => [...prev, msg]);
      setBody("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send");
    } finally { setSending(false); }
  };

  const startEdit = (m: PipelineMessage) => { setEditingId(m.id); setEditBody(m.body); };
  const cancelEdit = () => { setEditingId(null); setEditBody(""); };

  const saveEdit = async (m: PipelineMessage) => {
    const next = editBody.trim();
    if (!next || savingEdit) return;
    if (next === m.body) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const updated = await api.patch<PipelineMessage>(`/lead-pipeline/interests/${interestId}/messages/${m.id}`, { body: next });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      cancelEdit();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to edit");
    } finally { setSavingEdit(false); }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, m: PipelineMessage) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(m); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  return (
    <div className="flex flex-col">
      {/* Thread */}
      <div className="rounded-xl border border-border/60 bg-muted/15 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-8"><Loader size="sm" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <MessageSquare className="h-9 w-9 text-muted-foreground/25 mb-2" />
            <p className="text-sm text-muted-foreground">No messages yet.</p>
            <p className="text-xs text-muted-foreground/60">
              {buyerSuspended
                ? `This buyer is ${statusLabel(buyerStatus).toLowerCase()}. No actions can be taken.`
                : canMessage ? "Send the first message below." : "Messaging unlocks once you accept the assignment."}
            </p>
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m) => {
              const isAdvisor = m.sender_role === "bl_advisor" || m.sender_role === "admin";
              const mine = user?.id === m.sender_id;
              return (
                <div key={m.id} className={cn("flex flex-col gap-0.5", isAdvisor ? "items-end" : "items-start")}>
                  <span className="text-[0.6rem] text-muted-foreground px-1">
                    {m.sender_name} · {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {m.edited_at && <span className="italic"> · edited</span>}
                  </span>
                  {editingId === m.id ? (
                    <div className="w-full max-w-[85%] flex flex-col gap-1.5">
                      <AutoGrowTextarea
                        autoFocus
                        rows={2}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, m)}
                        className="w-full rounded-xl border border-primary/40 bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={cancelEdit} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors">Cancel</button>
                        <Button size="sm" disabled={!editBody.trim() || savingEdit} onClick={() => saveEdit(m)} className="h-7 px-3 text-[11px]">Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("flex items-center gap-1.5", isAdvisor ? "flex-row-reverse" : "flex-row")}>
                      <div className={cn(
                        "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed",
                        isAdvisor
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-white border border-border text-foreground rounded-tl-sm shadow-sm"
                      )}>
                        {m.body}
                      </div>
                      {mine && (
                        <button
                          onClick={() => startEdit(m)}
                          title="Edit message"
                          aria-label="Edit message"
                          className="shrink-0 h-6 w-6 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                  {isAdvisor && !m.is_read_by_buyer && (
                    <span className="text-[0.58rem] text-muted-foreground/60 px-1">Unread</span>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        {/* Compose — replaced by a notice when the buyer's account is deactivated */}
        {buyerSuspended ? (
          <div className="border-t border-border/60 bg-rose-500/10 px-3 py-2.5">
            <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-rose-600">
              <X className="h-3.5 w-3.5" />
              This buyer is {statusLabel(buyerStatus).toLowerCase()}. No actions can be taken.
            </p>
          </div>
        ) : canMessage && (
          <div className="border-t border-border/60 bg-white px-3 py-2.5 flex items-end gap-2">
            <AutoGrowTextarea
              rows={2}
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
              placeholder="Write a message to the buyer…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <Button size="sm" disabled={sending || !body.trim()} onClick={send} className="shrink-0 h-9">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Assignment detail modal ───────────────────────────────────────────────
function AssignmentDetailModal({
  assignment,
  onClose,
  onResponded,
}: {
  assignment: Assignment;
  onClose: () => void;
  onResponded: () => void;
}) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canPublish = hasRole("bl_advisor", "admin");

  const [status, setStatus] = useState(assignment.assignment_status);
  const [mode, setMode] = useState<"view" | "reject" | "publish">("view");
  // Full read-only lead details overlay (opened from the "View Details" action).
  const [showDetails, setShowDetails] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  // Lead detail
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [leadLoading, setLeadLoading] = useState(true);

  // Publish checklist
  const [publishChecklist, setPublishChecklist] = useState<{
    owner_connected: boolean | null;
    due_diligence_completed: boolean | null;
    nda_signed: boolean | null;
  }>({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
  const [publishNotes, setPublishNotes] = useState("");

  const allChecklistYes =
    publishChecklist.owner_connected === true &&
    publishChecklist.due_diligence_completed === true &&
    publishChecklist.nda_signed === true;

  useEffect(() => {
    api.get<LeadDetail>(`/lead-pipeline/${assignment.pipeline_id}`)
      .then(setLead)
      .catch(() => {})
      .finally(() => setLeadLoading(false));
  }, [assignment.pipeline_id]);

  const handlePublish = () => {
    setPublishChecklist({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
    setPublishNotes("");
    setMode("publish");
  };

  const confirmPublish = () => {
    if (!lead || !allChecklistYes) {
      toast.error("Please confirm all three checklist items before publishing.");
      return;
    }
    navigate("/listings/new", {
      state: {
        leadPrefill: lead,
        advisorChecklist: {
          owner_connected: true,
          due_diligence_completed: true,
          nda_signed: true,
          notes: publishNotes.trim() || null,
        },
      },
    });
    onClose();
  };

  const respond = async (action: "accept" | "reject") => {
    setSubmitting(true);
    try {
      await api.post(`/lead-pipeline/advisor/assignments/${assignment.id}/respond`, {
        action,
        note: action === "reject" ? (rejectNote.trim() || null) : null,
      });
      onResponded();
      if (action === "accept") {
        toast.success("Assignment accepted — you can now message the buyer.");
        setStatus("accepted");
        setMode("view");
      } else {
        toast.success("Assignment rejected — the system will reassign it.");
        onClose();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to respond");
    } finally { setSubmitting(false); }
  };

  const buyerSuspended = isDeactivated(assignment.buyer.status);
  const canMessage = status === "accepted" && !buyerSuspended;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 py-4 border-b border-border bg-white">
          <div className="h-11 w-11 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-bold text-primary">{initials(assignment.buyer)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground">{assignment.buyer.first_name} {assignment.buyer.last_name}</h2>
              <StatusPill status={status} />
              {buyerSuspended && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2.5 py-0.5 text-xs font-semibold text-rose-600 ring-1 ring-rose-500/30">
                  {statusLabel(assignment.buyer.status)}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {assignment.pipeline_title}
              {assignment.pipeline_reference_code && (
                <span className="ml-1.5 font-mono text-muted-foreground/80">{assignment.pipeline_reference_code}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted transition-colors shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-muted/10">

          {/* Lead Details card */}
          <div>
            <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <Building2 className="h-3 w-3" /> Lead Details
            </p>
            {leadLoading ? (
              <div className="flex justify-center py-6 rounded-xl border border-border/60 bg-white">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : lead ? (
              <div className="rounded-xl border border-border/60 bg-white overflow-hidden">
                {/* Cover image */}
                <div className="relative h-36 w-full bg-gradient-to-br from-slate-100 to-slate-200 shrink-0">
                  {lead.image_url ? (
                    <img src={lead.image_url} alt={lead.title ?? ""} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Building2 className="h-10 w-10 text-slate-300" />
                    </div>
                  )}
                  {/* Badges */}
                  {(lead.industry_category || lead.potential_category) && (
                    <div className="absolute left-3 top-3 flex flex-col gap-1">
                      {lead.industry_category && (
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                          {lead.industry_category}
                        </span>
                      )}
                      {lead.potential_category && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                          <Tag className="h-2.5 w-2.5" />{lead.potential_category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="absolute right-3 top-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,hsl(28_92%_55%),hsl(15_88%_52%))] px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                      <Sparkles className="h-3 w-3" />Coming Soon
                    </span>
                  </div>
                </div>
                {/* Info */}
                <div className="p-3.5 space-y-1.5">
                  <h3 className="text-sm font-extrabold text-foreground leading-snug">
                    {lead.title || lead.company_name || "Off-market opportunity"}
                  </h3>
                  {lead.reference_code && (
                    <p className="font-mono text-[0.65rem] text-muted-foreground">{lead.reference_code}</p>
                  )}
                  {lead.business_description && (
                    <p className="text-xs text-muted-foreground line-clamp-3 leading-5">{lead.business_description}</p>
                  )}
                  {lead.location && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-0.5">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{lead.location}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-border/60 bg-white p-4 text-xs text-muted-foreground text-center">
                Lead details unavailable.
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="rounded-xl border border-border/60 bg-white p-3.5">
            <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Buyer Contact</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <a href={`mailto:${assignment.buyer.email}`} className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                <Mail className="h-3.5 w-3.5" />{assignment.buyer.email}
              </a>
              {assignment.buyer.phone ? (
                <a href={`tel:${assignment.buyer.phone}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                  <Phone className="h-3.5 w-3.5" />{formatPhone(assignment.buyer.phone)}
                </a>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground/50">
                  <Phone className="h-3.5 w-3.5" />No phone provided
                </span>
              )}
            </div>
          </div>

          {/* Buyer's initial message */}
          {assignment.buyer_message && (
            <div className="rounded-xl border border-border/60 bg-white p-3.5">
              <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" /> Buyer's Initial Message
              </p>
              <p className="text-sm text-foreground leading-relaxed">{assignment.buyer_message}</p>
            </div>
          )}

          {/* Conversation */}
          <div>
            <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <MessageSquare className="h-3 w-3" /> Conversation
            </p>
            <MessageThread interestId={assignment.id} canMessage={canMessage} buyerSuspended={buyerSuspended} buyerStatus={assignment.buyer.status} />
          </div>

          {/* History */}
          {assignment.assignment_history.length > 0 && (
            <div className="rounded-xl border border-border/60 bg-white p-3.5">
              <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Assignment History</p>
              <div className="space-y-1.5">
                {assignment.assignment_history.map((h) => (
                  <div key={h.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                    <span className="font-medium text-foreground">{h.advisor_name}</span>
                    <span>— {h.action}</span>
                    {h.note && <span className="italic text-slate-400 line-clamp-1">"{h.note}"</span>}
                    <span className="ml-auto tabular-nums shrink-0">{new Date(h.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {assignment.assigned_at && (
            <p className="text-xs text-muted-foreground px-0.5">
              <span className="font-medium">Assigned:</span> {new Date(assignment.assigned_at).toLocaleString()}
            </p>
          )}
        </div>

        {/* Footer / actions */}
        {(status === "pending" || (status === "accepted" && canPublish && mode !== "publish")) && (
          <div className="border-t border-border bg-white px-5 py-4">
            {mode === "view" && status === "pending" ? (
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  className="text-destructive border-destructive/40 hover:bg-destructive/5 hover:border-destructive"
                  disabled={submitting}
                  onClick={() => setMode("reject")}
                >
                  <ThumbsDown className="h-4 w-4 mr-1.5" /> Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={submitting}
                  onClick={() => respond("accept")}
                >
                  <ThumbsUp className="h-4 w-4 mr-1.5" /> Accept
                </Button>
              </div>
            ) : mode === "view" && status === "accepted" && canPublish ? (
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDetails(true)} disabled={!lead}>
                  <Eye className="h-4 w-4 mr-1.5" /> View Details
                </Button>
                <Button onClick={handlePublish} disabled={!lead}>
                  <Upload className="h-4 w-4 mr-1.5" /> Publish as Listing
                </Button>
              </div>
            ) : mode === "reject" ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">
                    Reason for rejecting <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <textarea
                    rows={2} autoFocus
                    className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 transition resize-none"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    placeholder="The system will reassign this to another advisor…"
                  />
                </div>
                <div className="flex items-center justify-end gap-3">
                  <Button variant="outline" disabled={submitting} onClick={() => { setMode("view"); setRejectNote(""); }}>
                    Cancel
                  </Button>
                  <Button variant="destructive" disabled={submitting} onClick={() => respond("reject")}>
                    <ThumbsDown className="h-4 w-4 mr-1.5" /> Confirm Reject
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Publish checklist */}
        {mode === "publish" && (
          <div className="border-t border-border bg-white px-5 py-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardCheck className="h-4 w-4 text-primary" />
              <p className="text-sm font-bold text-foreground">Pre-Publish Checklist</p>
            </div>
            {([
              { key: "owner_connected", label: "Have you connected with the owner?" },
              { key: "due_diligence_completed", label: "Have you completed all due diligence?" },
              { key: "nda_signed", label: "Is the NDA signed with the owner?" },
            ] as const).map((q) => (
              <div key={q.key} className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2.5">
                <span className="text-xs text-foreground">{q.label}</span>
                <div className="flex shrink-0 gap-1.5">
                  {([{ val: true, text: "Yes" }, { val: false, text: "No" }] as const).map((opt) => {
                    const active = publishChecklist[q.key] === opt.val;
                    return (
                      <button
                        key={opt.text}
                        type="button"
                        onClick={() => setPublishChecklist((p) => ({ ...p, [q.key]: opt.val }))}
                        className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${
                          active
                            ? opt.val ? "bg-green-600 text-white" : "bg-rose-600 text-white"
                            : "bg-muted text-muted-foreground hover:bg-muted/70"
                        }`}
                      >
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <textarea
              rows={2}
              className="w-full resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
              value={publishNotes}
              onChange={(e) => setPublishNotes(e.target.value)}
              placeholder="Additional notes (optional)…"
            />
            {!allChecklistYes && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
                All three items must be confirmed <span className="font-bold">Yes</span> before you can publish.
              </p>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setMode("view")}>Cancel</Button>
              <Button onClick={confirmPublish} disabled={!allChecklistYes}>
                <Upload className="h-4 w-4 mr-1.5" /> Continue to Listing
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Full lead details overlay (sits above the assignment modal) */}
      {showDetails && lead && (
        <div
          className="absolute inset-0 z-[60] flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setShowDetails(false); }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative max-h-[90dvh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cover image */}
            <div className="relative h-48 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
              {lead.image_url ? (
                <img src={lead.image_url} alt={lead.title ?? ""} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Building2 className="h-14 w-14 text-slate-300" />
                </div>
              )}
              <div className="absolute right-3 top-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-muted-foreground shadow-sm backdrop-blur-sm hover:bg-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                {lead.industry_category && (
                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                    {lead.industry_category}
                  </span>
                )}
                {lead.potential_category && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                    <Tag className="h-2.5 w-2.5" />{lead.potential_category}
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-[calc(90dvh-12rem)] overflow-y-auto p-6">
              <h2 className="text-xl font-extrabold leading-snug text-gray-900">
                {lead.title || lead.company_name || "Off-market opportunity"}
              </h2>
              {lead.reference_code && (
                <p className="mt-1 font-mono text-xs text-gray-400">{lead.reference_code}</p>
              )}
              {lead.location && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                  <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{lead.location}</span>
                </div>
              )}

              {lead.business_description && (
                <div className="mt-4">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Description
                  </p>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-gray-600">
                    {lead.business_description}
                  </p>
                </div>
              )}

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {lead.company_name && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Company</p>
                      <p className="truncate text-sm text-foreground" title={lead.company_name}>{lead.company_name}</p>
                    </div>
                  </div>
                )}
                {lead.owner_email && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Owner email</p>
                      <a href={`mailto:${lead.owner_email}`} className="block truncate text-sm text-primary hover:underline" title={lead.owner_email}>{lead.owner_email}</a>
                    </div>
                  </div>
                )}
                {lead.website_url && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                    <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Website</p>
                      <a href={lead.website_url} target="_blank" rel="noopener noreferrer" className="block truncate text-sm text-primary hover:underline" title={lead.website_url}>{lead.website_url}</a>
                    </div>
                  </div>
                )}
                {typeof lead.interest_count === "number" && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                    <Users className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Buyer interest</p>
                      <p className="text-sm text-foreground">{lead.interest_count} {lead.interest_count === 1 ? "buyer" : "buyers"}</p>
                    </div>
                  </div>
                )}
                {lead.created_at && (
                  <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                    <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Added</p>
                      <p className="text-sm text-foreground">{new Date(lead.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDetails(false)}>
                  Close
                </Button>
                {canPublish && (
                  <Button onClick={() => { setShowDetails(false); handlePublish(); }}>
                    <Upload className="mr-1 h-4 w-4" />
                    Publish as Listing
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdvisorAssignmentsPage() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [activeId, setActiveId] = useState<number | null>(null);

  const load = async () => {
    try {
      const data = await api.get<Assignment[]>("/lead-pipeline/advisor/assignments");
      setAssignments(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = assignments.filter((a) => filterStatus === "all" || a.assignment_status === filterStatus);
  const pending  = assignments.filter((a) => a.assignment_status === "pending").length;
  const accepted = assignments.filter((a) => a.assignment_status === "accepted").length;
  const active = activeId !== null ? assignments.find((a) => a.id === activeId) ?? null : null;

  if (loading) return <Loader />;

  return (
    <PageShell>
      {/* Hero — matches the Coming Soon page style */}
      <section className="px-0">
        <div className="public-hero-shell mx-auto rounded-[1.85rem] px-6 py-8 shadow-glow sm:px-8 sm:py-9">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
          <div className="relative z-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <div className="public-pill">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Coming Soon Pipeline
              </div>
              <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl lg:text-5xl">
                My <span className="text-gradient">Assignments</span>
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                Buyer interests from the coming-soon pipeline assigned to you. Open one to review, message the
                buyer, and accept or reject.
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <span className="public-pill border-primary/25 bg-white/70 text-primary">
                  <ListChecks className="h-3.5 w-3.5" />
                  Assigned to You
                </span>
                <span className="public-pill bg-white/72">
                  <ThumbsUp className="h-3.5 w-3.5" />
                  Accept or Reject
                </span>
                <span className="public-pill bg-white/72">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Message Buyers
                </span>
              </div>
            </div>

            {/* Snapshot card */}
            <div className="public-glass-card rounded-[1.35rem] p-5">
              <h2 className="text-lg font-bold text-foreground">Assignment snapshot</h2>
              <div className="mt-3 grid grid-cols-3 gap-2.5">
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Pending</div>
                  <div className="mt-1.5 text-xl font-extrabold text-amber-600">{pending}</div>
                </div>
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Accepted</div>
                  <div className="mt-1.5 text-xl font-extrabold text-emerald-600">{accepted}</div>
                </div>
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total</div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{assignments.length}</div>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-primary/10 bg-white/60 p-4">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">How it works</p>
                <ul className="mt-2 space-y-2">
                  {[
                    { icon: ListChecks, text: "Review the buyer's interest" },
                    { icon: ThumbsUp, text: "Accept to connect, or reject to reassign" },
                    { icon: MessageSquare, text: "Message the buyer once accepted" },
                  ].map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assignments list */}
      <PagePanel>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Assignments</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} of {assignments.length} shown</p>
          </div>
          <select
            className="rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <EmptyPanel
            icon={Building2}
            title={filterStatus === "all" ? "No assignments yet" : `No ${filterStatus} assignments`}
            description="You'll see buyer interests here once the admin assigns them to you."
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => (
              <button
                key={a.id}
                onClick={() => setActiveId(a.id)}
                className="group w-full text-left rounded-xl border border-border bg-background p-4 transition-all hover:border-primary/40 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <div className="flex items-center gap-4">
                  {/* Buyer avatar */}
                  <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary">{initials(a.buyer)}</span>
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 min-w-0">
                      <span className="shrink-0"><StatusPill status={a.assignment_status} /></span>
                      <span className="text-sm font-semibold text-foreground truncate min-w-0">{a.pipeline_title}</span>
                      {a.pipeline_reference_code && (
                        <span className="shrink-0 font-mono text-[0.7rem] font-normal text-muted-foreground">{a.pipeline_reference_code}</span>
                      )}
                    </div>
                    <p className="text-sm text-foreground flex items-center gap-1.5">
                      {a.buyer.first_name} {a.buyer.last_name}
                      {isDeactivated(a.buyer.status) && (
                        <span className="inline-flex items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-rose-600">
                          {statusLabel(a.buyer.status)}
                        </span>
                      )}
                    </p>
                    {a.buyer_message ? (
                      <div className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                        <MessageSquare className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary/60" />
                        <span className="line-clamp-1 italic">"{a.buyer_message}"</span>
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground/70 truncate">{a.buyer.email}</p>
                    )}
                  </div>

                  {/* Affordance */}
                  <div className="shrink-0 flex items-center gap-1 text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors">
                    {a.assignment_status === "pending" && (
                      <span className="hidden sm:inline rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.65rem] font-bold text-amber-600">Action needed</span>
                    )}
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </PagePanel>

      {/* Detail modal */}
      {active && (
        <AssignmentDetailModal
          assignment={active}
          onClose={() => setActiveId(null)}
          onResponded={load}
        />
      )}
    </PageShell>
  );
}
